import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ChevronDown, Instagram, Minus, Plus, Ruler, TriangleAlert, X } from "lucide-react";
import { api } from "@/lib/api";
import { inr, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "@/lib/content";
import { useCart } from "@/lib/cart";
import { track } from "@/lib/metaPixel";
import { gTrack } from "@/lib/ga";
import { Crosshair } from "@/components/Reveal";
import { LogoMark } from "@/components/Logo";

const FABRIC_INFO = {
  THIN: "THIN — LIGHTWEIGHT, BREATHABLE COTTON FOR MILD DAYS AND LAYERING",
  FLEECE: "FLEECE — SOFT BRUSHED INSIDE, WARM AND PLUSH FOR WINTER",
};

// StrictMode-safe ViewContent dedup: only the first load of a product tracks.
let lastViewedId = null;

function Accordion({ id, title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/10">
      <button
        data-testid={`accordion-${id}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-bone/80 transition-colors hover:text-bone"
      >
        {title}
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180 text-gold" : ""}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-6 text-sm leading-relaxed text-bone/65">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ChartTable({ chart, id = "size-chart" }) {
  const garment = chart.some((r) => r.hips_in || r.length_in);
  return (
    <table className="w-full border-t border-line font-mono text-xs text-bone/70" data-testid={`${id}-table`}>
      <thead>
        <tr className="border-b border-line text-[10px] uppercase tracking-[0.2em] text-bone/45">
          <th className="px-5 py-3 text-left font-medium">LABELLED SIZE</th>
          {garment ? (
            <>
              <th className="px-5 py-3 text-left font-medium">WAIST (IN)</th>
              <th className="px-5 py-3 text-left font-medium">HIPS (IN)</th>
              <th className="px-5 py-3 text-left font-medium">LENGTH (IN)</th>
            </>
          ) : (
            <>
              <th className="px-5 py-3 text-left font-medium">WAIST (IN)</th>
              <th className="px-5 py-3 text-left font-medium">WAIST (CM)</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {chart.map((row) => (
          <tr key={row.label} className="border-b border-line/50 last:border-0">
            <td className="px-5 py-3 text-bone">{row.label}</td>
            {garment ? (
              <>
                <td className="px-5 py-3">{row.waist_in}″</td>
                <td className="px-5 py-3">{row.hips_in}″</td>
                <td className="px-5 py-3">{row.length_in}″</td>
              </>
            ) : (
              <>
                <td className="px-5 py-3">{row.waist_in}</td>
                <td className="px-5 py-3">{row.waist_cm}</td>
              </>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SizeChart({ chart }) {
  const [open, setOpen] = useState(false);
  const garment = chart.some((r) => r.hips_in || r.length_in);
  return (
    <div className="mt-6 border border-line">
      <button
        data-testid="size-chart-toggle"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/80 transition-colors hover:text-gold"
      >
        {garment ? "SIZE CHART — JEANS MEASUREMENTS (IN)" : "SIZE CHART — WAIST MEASUREMENTS"}
        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180 text-gold" : ""}`} strokeWidth={1.5} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <ChartTable chart={chart} id="size-chart" />
            <p className="border-t border-line px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/40">
              Flat-lay measurements, ±0.5". {garment ? "Japanese sizing runs small — choose one size higher than your usual India size." : "The labelled size equals your waist in inches."}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SizeGuideModal({ chart, open, onClose }) {
  const garment = chart.some((r) => r.hips_in || r.length_in);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="size-guide-backdrop"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-ink/80 px-4 backdrop-blur-sm"
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Size guide"
            data-testid="size-guide-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative max-h-[86vh] w-full max-w-md overflow-y-auto border border-line bg-surface p-7 shadow-[0_30px_80px_rgba(0,0,0,0.6)] sm:p-9"
          >
            <button
              type="button"
              data-testid="size-guide-close"
              aria-label="Close size guide"
              onClick={onClose}
              className="absolute right-4 top-4 p-1 text-bone/40 transition-colors hover:text-gold"
            >
              <X className="h-4 w-4" strokeWidth={1.5} />
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">SIZE GUIDE</p>
            <h3 className="mt-3 font-display text-3xl leading-[0.95] text-bone sm:text-4xl">FIND YOUR FIT.</h3>
            {garment && (
              <ol className="mt-6 space-y-4">
                {[
                  ["01 — WAIST", "Measure around your natural waist, where you'd actually wear the jeans. Keep the tape snug — not tight."],
                  ["02 — HIPS", "Measure around the fullest part of your hips, usually 7–9 inches below your waist."],
                  ["03 — LENGTH", "Lay a pair of jeans that fit you well flat, and measure from the waistband down to the leg opening."],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <span className="mt-0.5 shrink-0 font-mono text-[10px] tracking-[0.2em] text-gold">{t}</span>
                    <span className="text-sm leading-relaxed text-bone/70">{d}</span>
                  </li>
                ))}
              </ol>
            )}
            <div className="mt-6 border border-line">
              <ChartTable chart={chart} id="size-guide" />
            </div>
            {garment && (
              <p className="mt-4 border border-gold/30 bg-gold/5 px-4 py-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-gold">
                BETWEEN SIZES — OR ABOVE THE BIGGEST ROW? GO ONE SIZE UP. JAPANESE SIZING RUNS SMALLER THAN INDIAN SIZING.
              </p>
            )}
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.2em] text-bone/40">
              MEASURE OVER LIGHT CLOTHING, STANDING RELAXED, IN INCHES.
            </p>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="size-guide-dm"
              className="mt-3 inline-block font-mono text-[9px] uppercase tracking-[0.2em] text-gold underline decoration-gold/50 underline-offset-4 hover:text-bone"
            >
              STILL UNSURE? DM US {INSTAGRAM_HANDLE}
            </a>
            <button
              type="button"
              data-testid="size-guide-got-it"
              onClick={onClose}
              className="mt-6 w-full bg-gold py-3.5 font-display text-lg tracking-[0.15em] text-ink transition-colors hover:bg-bone"
            >
              GOT IT
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [view, setView] = useState(0);
  const [size, setSize] = useState(null);
  const [fabric, setFabric] = useState("");
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = guideOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [guideOpen]);

  // Thumb-reachable add-to-bag: show the sticky bar once the inline CTA scrolls away (mobile only).
  const ctaRef = useRef(null);
  const [ctaVisible, setCtaVisible] = useState(true);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return undefined;
    let ticking = false;
    const update = () => {
      ticking = false;
      const r = el.getBoundingClientRect();
      // Bar appears only when the CTA has scrolled ABOVE the viewport — not while
      // it's merely below the fold on first paint.
      setCtaVisible(r.bottom > 0);
    };
    // One layout read per animation frame, never per scroll event.
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [product]);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setView(0);
    setSize(null);
    setFabric("");
    setQty(1);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setFabric(r.data.fabrics?.[0] || "");
        document.title = `${r.data.name} — REQUIEM FEVER`;
        if (lastViewedId !== r.data.id) {
          lastViewedId = r.data.id;
          track("ViewContent", {
            content_name: r.data.name,
            content_type: "product",
            content_ids: [r.data.id],
            currency: "INR",
            value: r.data.price,
          });
          gTrack("view_item", {
            currency: "INR",
            value: r.data.price,
            items: [{ item_id: r.data.slug, item_name: r.data.name, price: r.data.price }],
          });
        }
        const pre = searchParams.get("size");
        if (pre && r.data.sizes.some((s) => s.label === pre && s.stock > 0)) {
          setSize(pre);
        }
      })
      .catch(() => setNotFound(true));
  }, [slug, searchParams]);

  if (notFound) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-5 bg-ink px-6 text-center">
        <p className="font-display text-5xl tracking-wide text-bone">SOLD ITS LAST SOUL</p>
        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-bone/50">This piece could not be found.</p>
        <Link to="/shop" data-testid="product-not-found-back" className="mt-2 border border-gold px-8 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-gold hover:bg-gold hover:text-ink">
          BACK TO SHOP
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-[60vh] bg-ink pt-24 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-bone/40">
        LOADING…
      </div>
    );
  }

  const useVariants = product.sizes.length === 0;
  const options = useVariants ? product.variants : product.sizes;
  const selObj = options.find((s) => s.label === size);
  const nothingLeft = options.length > 0 && options.every((s) => s.stock === 0);
  const lowStock = selObj && selObj.stock > 0 && selObj.stock <= 3;
  const noPrice = product.price == null;
  const selPrice = selObj?.price || product.price;
  const onAdd = () => {
    if (options.length > 0 && !size) return;
    add({
      slug: product.slug,
      name: product.name,
      colour: product.colour,
      price: selPrice,
      size: useVariants ? "" : size,
      variant: useVariants ? size : fabric,
      image: product.images[0] || "",
      qty,
    });
    track("AddToCart", {
      content_name: product.name,
      content_type: "product",
      content_ids: [product.id],
      currency: "INR",
      value: selPrice * qty,
    });
    gTrack("add_to_cart", {
      currency: "INR",
      value: selPrice * qty,
      items: [{ item_id: product.slug, item_name: product.name, price: selPrice, quantity: qty }],
    });
    setOpen(true);
  };

  return (
    <div className="bg-ink" data-testid="product-page">
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8 sm:py-12">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/40">
          <Link to="/shop" data-testid="product-breadcrumb-shop" className="transition-colors hover:text-gold">SHOP</Link>
          <span className="mx-2">/</span>
          <span className="text-bone/70">{product.name}</span>
        </p>

        <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-7">
            <div className="relative overflow-hidden bg-surface" data-testid="product-gallery">
              <Crosshair className="left-3 top-3 border-l border-t" />
              <Crosshair className="bottom-3 right-3 border-b border-r" />
              {product.images.length > 0 ? (
                <AnimatePresence mode="wait">
                  <motion.img
                    key={view}
                    src={product.images[view]}
                    alt={`${product.name} — ${product.colour}, view ${view + 1}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className="block h-auto w-full"
                  />
                </AnimatePresence>
              ) : (
                <div className="flex aspect-square w-full flex-col items-center justify-center gap-4">
                  <LogoMark className="h-12 w-12 opacity-70" />
                  <p className="font-display text-3xl tracking-[0.1em] text-bone/80">IMAGE COMING SOON</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/45">
                    Campaign photography loading shortly
                  </p>
                </div>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="product-gallery-thumbs">
                {product.images.map((src, i) => (
                  <button
                    key={src}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setView(i)}
                    aria-label={`View image ${i + 1}`}
                    className={`aspect-square w-20 overflow-hidden border transition-colors sm:w-24 ${
                      view === i ? "border-gold" : "border-transparent hover:border-white/30"
                    }`}
                  >
                    <img src={src} alt="" className="h-full w-full object-contain" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                {product.collection === "evangelion-drop" ? "DROP 002" : "DROP 001"} — {product.colour}
              </p>
              <h1 className="mt-3 font-display text-4xl leading-[0.95] tracking-[0.01em] text-bone sm:text-5xl">
                {product.name}
              </h1>
              <p className="mt-4 font-mono text-lg text-bone" data-testid="product-price">
                {noPrice ? "PRICE TBA" : inr(selPrice)}
                {!noPrice && (
                  <span className="ml-3 text-[10px] uppercase tracking-[0.2em] text-bone/45">INCL. ALL TAXES</span>
                )}
              </p>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-bone/70">{product.description}</p>

              {product.fabrics?.length > 0 && (
                <div className="mt-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60">
                    FABRIC
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="fabric-options">
                    {product.fabrics.map((f) => (
                      <button
                        key={f}
                        data-testid={`fabric-option-${f}`}
                        onClick={() => setFabric(f)}
                        className={`h-10 min-w-14 border px-3 font-mono text-sm transition-colors ${
                          fabric === f
                            ? "border-gold bg-gold font-semibold text-ink"
                            : "border-line text-bone/80 hover:border-bone"
                        }`}
                      >
                        {f}
                      </button>
                    ))}
                  </div>
                  <p
                    className="mt-3 min-h-4 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-bone/50"
                    data-testid="fabric-description"
                  >
                    {FABRIC_INFO[fabric] || ""}
                  </p>
                </div>
              )}

              {options.length > 0 && (
                <>
                  <div className="mt-8 flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60">
                      SELECT {useVariants ? "VARIANT" : "SIZE"}
                    </p>
                    <a
                      href={INSTAGRAM_URL}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="size-help-instagram"
                      className="font-mono text-[10px] uppercase tracking-[0.2em] text-bone/45 underline decoration-gold/50 underline-offset-4 transition-colors hover:text-gold"
                    >
                      NEED HELP? DM US
                    </a>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="size-options">
                    {options.map((o) => {
                      const out = o.stock === 0;
                      return (
                        <button
                          key={o.label}
                          data-testid={`size-option-${o.label}`}
                          disabled={out}
                          onClick={() => {
                            setSize(o.label);
                            setQty(1);
                            if (useVariants && o.image) {
                              const idx = product.images.indexOf(o.image);
                              if (idx >= 0) setView(idx);
                            }
                          }}
                          className={`h-12 min-w-14 border px-3 font-mono text-sm transition-colors ${
                            out
                              ? "cursor-not-allowed border-line text-bone/25 line-through"
                              : size === o.label
                              ? "border-gold bg-gold font-semibold text-ink"
                              : "border-line text-bone/80 hover:border-bone"
                          }`}
                        >
                          {o.label}
                          {!out && o.price && o.price > product.price && (
                            <span className={`ml-1 text-[9px] ${size === o.label ? "text-ink/70" : "text-gold"}`}>
                              +₹{o.price - product.price}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 h-4 font-mono text-[10px] uppercase tracking-[0.25em]" data-testid="size-stock-note">
                    {selObj &&
                      !noPrice &&
                      (selObj.stock === 0 ? (
                        <span className="text-magenta">SOLD OUT</span>
                      ) : lowStock ? (
                        <span className="text-gold">LOW STOCK — ONLY {selObj.stock} LEFT</span>
                      ) : (
                        <span className="text-bone/50">
                          {useVariants ? "AVAILABLE" : "IN STOCK"} — READY TO SHIP
                        </span>
                      ))}
                  </p>
                  {product.size_chart.length > 0 && (
                    <button
                      type="button"
                      data-testid="size-guide-open"
                      onClick={() => setGuideOpen(true)}
                      className="mt-4 flex w-full items-center justify-between border border-line px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/80 transition-colors hover:border-gold hover:text-gold"
                    >
                      <span className="flex items-center gap-2.5">
                        <Ruler className="h-4 w-4 text-gold" strokeWidth={1.5} />
                        SIZE GUIDE — HOW TO MEASURE
                      </span>
                      <ChevronDown className="h-4 w-4 -rotate-90 text-gold" strokeWidth={1.5} />
                    </button>
                  )}
                  {!useVariants && (
                    <p
                      className="mt-4 flex items-start gap-2.5 border border-gold/30 bg-gold/5 px-4 py-3 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-gold"
                      data-testid="sizing-warning"
                    >
                      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                      <span>
                        JAPANESE SIZING RUNS SMALL — WE RECOMMEND CHOOSING ONE SIZE HIGHER THAN YOUR USUAL INDIA SIZE
                      </span>
                    </p>
                  )}
                </>
              )}

              <div ref={ctaRef} className="mt-6 flex gap-3">
                <div className="flex items-center border border-line" data-testid="quantity-selector">
                  <button
                    data-testid="qty-minus-button"
                    aria-label="Decrease quantity"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="p-3.5 text-bone/60 transition-colors hover:text-gold"
                  >
                    <Minus className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                  <span className="w-10 text-center font-mono text-sm text-bone" data-testid="qty-value">
                    {qty}
                  </span>
                  <button
                    data-testid="qty-plus-button"
                    aria-label="Increase quantity"
                    onClick={() => setQty(Math.min(10, qty + 1))}
                    className="p-3.5 text-bone/60 transition-colors hover:text-gold"
                  >
                    <Plus className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
                <button
                  data-testid="add-to-bag-button"
                  disabled={noPrice || nothingLeft}
                  onClick={() => {
                    if (options.length > 0 && !size) toast.error(`SELECT A ${useVariants ? "VARIANT" : "SIZE"} FIRST`);
                    else onAdd();
                  }}
                  className={`flex-1 py-4 font-display text-xl tracking-[0.15em] transition-colors duration-300 ${
                    noPrice || nothingLeft
                      ? "cursor-not-allowed bg-line text-bone/40"
                      : "bg-gold text-ink hover:bg-bone"
                  }`}
                >
                  {noPrice ? "PRICE TBA" : nothingLeft ? "SOLD OUT" : "ADD TO BAG"}
                </button>
              </div>

              {product.size_chart.length > 0 ? (
                <SizeChart chart={product.size_chart} />
              ) : product.fit_note ? (
                <p className="mt-6 border border-line px-5 py-3.5 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-bone/50">
                  FIT — {product.fit_note}
                </p>
              ) : null}

              <div className="mt-8">
                <Accordion id="shipping" title="SHIPPING — INDIA" defaultOpen>
                  Free shipping to every serviceable pin code in India. Dispatched within 2–3 business
                  days; estimated delivery within 15–20 days. Tracking link follows by email the moment
                  your order ships.
                </Accordion>
                <Accordion id="details-care" title="DETAILS & CARE">
                  <ul className="space-y-1.5">
                    {product.details.map((d) => (
                      <li key={d} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-violet" aria-hidden="true" />
                        {d}
                      </li>
                    ))}
                    <li className="pt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/45">CARE</li>
                    {product.care.map((c) => (
                      <li key={c} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </Accordion>
                <Accordion id="returns" title="RETURNS & EXCHANGES">
                  7-day return or exchange from delivery — unworn, unwashed, tags on. Size exchanges
                  depend on stock.{" "}
                  <Link to="/returns" data-testid="returns-policy-link" className="text-gold underline underline-offset-4">
                    Read the full policy
                  </Link>
                  .
                </Accordion>
              </div>

              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="size-help-box"
                className="mt-6 flex items-start gap-4 border border-violet/40 bg-violet/10 p-5 transition-colors hover:border-violet"
              >
                <Instagram className="mt-0.5 h-5 w-5 shrink-0 text-gold" strokeWidth={1.5} />
                <p className="font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-bone/70">
                  Not sure about your size? DM us on Instagram {INSTAGRAM_HANDLE} — we'll help you pick
                  the right fit.
                </p>
              </a>
            </div>
          </div>

          <AnimatePresence>
            {!ctaVisible && !noPrice && !nothingLeft && (
              <motion.div
                initial={{ y: 90 }}
                animate={{ y: 0 }}
                exit={{ y: 90 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface lg:hidden"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
                data-testid="mobile-add-to-bag-bar"
              >
                <div className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-[9px] uppercase tracking-[0.2em] text-bone/50">
                      {product.name}
                    </p>
                    <p className="font-display text-lg tracking-wide text-bone" data-testid="mobile-bar-price">
                      {inr(selPrice)}
                    </p>
                  </div>
                  <button
                    data-testid="mobile-add-to-bag-button"
                    onClick={() => {
                      if (options.length > 0 && !size) toast.error(`SELECT A ${useVariants ? "VARIANT" : "SIZE"} FIRST`);
                      else onAdd();
                    }}
                    className="shrink-0 bg-gold px-8 py-3.5 font-display text-lg tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-bone"
                  >
                    ADD TO BAG
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <SizeGuideModal chart={product.size_chart} open={guideOpen} onClose={() => setGuideOpen(false)} />
        </div>
      </div>
    </div>
  );
}
